// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  CompanySubcategory,
  IlliquidAssetCurrency,
  IlliquidAssetType,
  OtherIlliquidSubcategory,
  PeVcSubcategory,
  RealEstateSubcategory,
  TangibleSubcategory,
} from '@shared/data-access/types/engine-types';
import type { IlliquidAssetFormData } from '../types/illiquid-asset-form';

export const buildIlliquidAssetDefaultValues = (
  todayIso: string,
): IlliquidAssetFormData => ({
  category: IlliquidAssetType.RealEstate,
  name: '',
  subcategory: RealEstateSubcategory.OwnerOccupiedHome,
  description: '',

  acquisition_date: todayIso,
  acquisition_value: 0,
  currency: IlliquidAssetCurrency.EUR,
  ownership_pct: 1,

  valuation_date: todayIso,
  current_valuation: 0,
});

export const buildSubcategoryOptionsByAssetType = () =>
  new Map<IlliquidAssetType, readonly IlliquidAssetFormData['subcategory'][]>([
    [
      IlliquidAssetType.RealEstate,
      [
        RealEstateSubcategory.OwnerOccupiedHome,
        RealEstateSubcategory.RentalProperty,
        RealEstateSubcategory.SecondResidence,
        RealEstateSubcategory.CommercialPremises,
        RealEstateSubcategory.Office,
        RealEstateSubcategory.IndustrialLogistics,
        RealEstateSubcategory.HotelHostel,
        RealEstateSubcategory.UrbanLand,
        RealEstateSubcategory.RusticAgriculturalLand,
        RealEstateSubcategory.RusticFarm,
        RealEstateSubcategory.RealEstateDevelopment,
        RealEstateSubcategory.OtherRealEstate,
      ],
    ],
    [
      IlliquidAssetType.Company,
      [
        CompanySubcategory.FamilyBusiness,
        CompanySubcategory.PrivateCompany,
        CompanySubcategory.Holding,
        CompanySubcategory.RealEstateCompany,
        CompanySubcategory.OwnBusiness,
        CompanySubcategory.Other,
      ],
    ],
    [
      IlliquidAssetType.PeVc,
      [
        PeVcSubcategory.PrivateEquityFund,
        PeVcSubcategory.VentureCapitalFund,
        PeVcSubcategory.DirectInvestmentStartup,
        PeVcSubcategory.ClubDeal,
        PeVcSubcategory.Spv,
        PeVcSubcategory.Other,
      ],
    ],
    [
      IlliquidAssetType.Tangible,
      [
        TangibleSubcategory.Art,
        TangibleSubcategory.Jewelry,
        TangibleSubcategory.Watches,
        TangibleSubcategory.ClassicCars,
        TangibleSubcategory.Collectibles,
        TangibleSubcategory.Other,
      ],
    ],
    [
      IlliquidAssetType.OtherIlliquid,
      [
        OtherIlliquidSubcategory.RoyaltiesRights,
        OtherIlliquidSubcategory.Concessions,
        OtherIlliquidSubcategory.CryptoTokensBlocked,
        OtherIlliquidSubcategory.Other,
      ],
    ],
  ]);

export const buildDefaultSubcategoryByAssetType = () =>
  new Map<IlliquidAssetType, IlliquidAssetFormData['subcategory']>([
    [IlliquidAssetType.RealEstate, RealEstateSubcategory.OwnerOccupiedHome],
    [IlliquidAssetType.Company, CompanySubcategory.FamilyBusiness],
    [IlliquidAssetType.PeVc, PeVcSubcategory.PrivateEquityFund],
    [IlliquidAssetType.Tangible, TangibleSubcategory.Art],
    [IlliquidAssetType.OtherIlliquid, OtherIlliquidSubcategory.RoyaltiesRights],
  ]);
